import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useOverlay } from "@toss/use-overlay";
import { Box } from "@chakra-ui/react";
import { OverlayModal, Button, Spacing, ColorIcon } from "goi_common";
import { baseColors, BaseStack } from "goi_common/src/design-system";
import { useFomoState, checkQuizCouponActive } from "@/hooks/useFomoState";
import { sessionStorage, storageKeys } from "@/utils/storage";
import { FomoCountdownBanner } from "./FomoCountdownBanner";
import { FomoReassuranceBanner } from "./FomoReassuranceBanner";

export const FOMO_EXCLUDED_ROUTES = [
  "success",
  "failure",
  "contract-check",
  "refund",
  "login",
  "info-trust",
];

export function FomoBanner() {
  const router = useRouter();
  const overlay = useOverlay();
  const modalOpenedRef = useRef(false);
  const { status, remainingMs, isModalShown, markModalShown, startCountdown } =
    useFomoState();
  const isTossCampaign =
    sessionStorage.get(storageKeys.AdsDiscountCampaignIsToss) === "true";
  const isTmCampaign =
    sessionStorage.get(storageKeys.AdsDiscountCampaignIsTm) === "true";

  const openModal = useCallback(() => {
    overlay.open(({ isOpen, close }) => {
      const handleClose = () => {
        close();
        markModalShown();
        startCountdown();
      };

      return (
        <OverlayModal
          onClose={handleClose}
          isOpen={isOpen}
          modalContentStyle={{
            maxWidth: "360px",
            margin: "0 16px",
            minHeight: "none",
            borderRadius: "16px",
          }}
          overlayModalContent={
            <BaseStack
              px={20}
              py={20}
              spacing={0}
              alignItems="center"
              width="100%"
            >
              <Box
                className="body_2_normal_r"
                textAlign="center"
                color={baseColors.label_alternative_1}
              >
                {isTossCampaign
                  ? "토스에서 방문해주신"
                  : isTmCampaign
                  ? "상담 요청을 해주신"
                  : "다시 고이를 찾아주신"}
              </Box>
              <Spacing gap={4} />
              <Box
                className="headline_2_sb"
                textAlign="center"
                color={baseColors.label_normal}
              >
                고객님에게만 보이는 가격 할인
              </Box>

              <Spacing gap={24} />

              {!isTmCampaign && (
                <>
                  <BaseStack
                    direction="horizontal"
                    spacing={8}
                    alignItems="center"
                  >
                    <Box
                      background={baseColors.red_100}
                      borderRadius="8px"
                      minWidth="64px"
                      textAlign="center"
                      px="16px"
                      py="12px"
                    >
                      <Box
                        className="title_2_b"
                        color={baseColors.status_negative}
                      >
                        30
                      </Box>
                    </Box>
                    <Box
                      className="title_2_b"
                      color={baseColors.label_assistive_1}
                    >
                      :
                    </Box>
                    <Box
                      background={baseColors.red_100}
                      borderRadius="8px"
                      minWidth="64px"
                      textAlign="center"
                      px="16px"
                      py="12px"
                    >
                      <Box
                        className="title_2_b"
                        color={baseColors.status_negative}
                      >
                        00
                      </Box>
                    </Box>
                  </BaseStack>
                  <Spacing gap={8} />
                  <BaseStack direction="horizontal" spacing={8}>
                    <Box
                      minWidth="64px"
                      textAlign="center"
                      className="label_2_sb"
                      color={baseColors.label_assistive_1}
                    >
                      분
                    </Box>
                    <Box width="16px" />
                    <Box
                      minWidth="64px"
                      textAlign="center"
                      className="label_2_sb"
                      color={baseColors.label_assistive_1}
                    >
                      초
                    </Box>
                  </BaseStack>
                  <Spacing gap={16} />
                </>
              )}

              <Box
                width="100%"
                background={baseColors.fill_normal}
                borderRadius="6px"
                px="20px"
                py="20px"
              >
                <BaseStack alignItems="center" spacing={0}>
                  {!isTmCampaign && (
                    <Box
                      className="body_1_normal_sb"
                      textAlign="center"
                      color={baseColors.label_alternative_1}
                    >
                      지금부터 30분 동안
                    </Box>
                  )}
                  <Box
                    className="body_1_normal_sb"
                    textAlign="center"
                    color={baseColors.blue_400}
                  >
                    10만원 할인 혜택을 추가로 드려요!
                  </Box>
                  <Spacing gap={12} />
                  <ColorIcon name="icon_Graphic_PriceDrop" size={80} color="" />
                  {!isTmCampaign && (
                    <>
                      <Spacing gap={12} />
                      <Box
                        className="label_1_r"
                        textAlign="center"
                        color={baseColors.label_alternative_1}
                      >
                        지금 놓치면 사라지는
                        <br />딱 한 번뿐인 추가 할인 이벤트.
                      </Box>
                    </>
                  )}
                </BaseStack>
              </Box>

              <Spacing gap={24} />

              <Button
                css={{
                  background: baseColors.primary_orange,
                  borderRadius: "8px",
                  width: "100%",
                  height: "52px",
                }}
                className="white button_16_sb"
                onClick={handleClose}
              >
                확인
              </Button>
            </BaseStack>
          }
        />
      );
    });
  }, [overlay, markModalShown, startCountdown, isTossCampaign, isTmCampaign]);

  useEffect(() => {
    if (router.pathname !== "/") return;
    if (checkQuizCouponActive()) return;
    if (status !== "inactive" && !isModalShown && !modalOpenedRef.current) {
      modalOpenedRef.current = true;
      openModal();
    }
  }, [router.pathname, status, isModalShown, openModal]);

  const isExcludedRoute = FOMO_EXCLUDED_ROUTES.some((r) =>
    router.route.includes(r)
  );

  if (isExcludedRoute) return null;

  // 홈에서만 quiz coupon floating 배너 노출 (퍼널은 FomoInlineBanner가 담당)
  if (router.pathname === "/" && checkQuizCouponActive()) {
    return <FomoReassuranceBanner variant="quiz_coupon" />;
  }

  if (router.pathname !== "/" || status === "inactive" || status === "pending")
    return null;

  if (status === "reassurance") return <FomoReassuranceBanner />;

  return <FomoCountdownBanner remainingMs={remainingMs} />;
}
